import { useState } from 'react'
import { useSignIn } from '@clerk/clerk-react'
import { useNavigate, Link } from 'react-router-dom'
import { ChevronLeft, ArrowRight, Loader2, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { BrainLogo } from '@/components/ui/brain-logo'

export default function SignInPage() {
  const { signIn, setActive, isLoaded } = useSignIn()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded || !signIn) return

    setIsEmailLoading(true)
    setError(null)

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      })

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        navigate('/onboarding')
      } else {
        // Handle additional steps if needed
        setError('Please complete the sign-in process')
      }
    } catch (err: any) {
      const error = err.errors?.[0]
      const errorCode = error?.code || ''
      const errorMessage = error?.message || ''
      
      // Check if this is an OAuth account trying to use password login
      // Clerk returns "strategy_for_user_invalid" code with "Invalid verification strategy" message
      // when user tries password login for OAuth account
      if (errorCode === 'strategy_for_user_invalid' || errorMessage === 'Invalid verification strategy') {
        setError(
          'This account was created with Google sign-in. Please use "Log In with Google" button below to sign in.'
        )
      } else {
        setError(errorMessage || 'Failed to sign in. Please check your credentials.')
      }
    } finally {
      setIsEmailLoading(false)
    }
  }

  const handleOAuth = async (strategy: 'oauth_google') => {
    if (!isLoaded || !signIn) {
      setError('Authentication service is not ready. Please try again.')
      return
    }

    setError(null)
    setIsGoogleLoading(true)

    try {
      const baseUrl = window.location.origin
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: `${baseUrl}/sso-callback`,
        redirectUrlComplete: `${baseUrl}/onboarding`,
      })
    } catch (err: any) {
      console.error('OAuth error:', err)
      setError(err.errors?.[0]?.message || `Failed to sign in with Google. Please try again.`)
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Section - Branding */}
      <div className="hidden lg:flex relative w-[504px] bg-[#2b42a3] overflow-hidden">
        <div className="absolute inset-0 bg-[#414fbf] opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="mb-8">
              <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
                <ChevronLeft className="h-4 w-4" />
                <span>Return Home</span>
              </Link>
            </div>
            
            <div className="mb-8">
              <BrainLogo size="lg" variant="dark" showGlow={false} />
            </div>
            
            <div className="mb-6">
              <h1 className="text-[39px] font-black leading-[47px] mb-4">
                Back With Your Expert Community
              </h1>
              <p className="text-[#bdbdbd] text-base leading-[28px]">
                Sign in to access your workspace, connect with the community, and continue building with us.
              </p>
            </div>
          </div>
        </div>
        <img
          src="/sign_in_image.png"
          alt="Sign in illustration"
          className="absolute bottom-0 left-0 right-0 w-full h-auto object-contain"
        />
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
            <ChevronLeft className="h-4 w-4" />
            <span>Return Home</span>
          </Link>
          <Link to="/sign-up" className="text-sm text-slate-600">
            <span className="font-normal">New User? </span>
            <span className="font-bold underline">SIGN UP HERE</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-8 pb-0 lg:p-12 lg:pb-0 bg-[#F8FAFC]">
          <div className="w-full max-w-[460px]">
            <div className="border border-slate-200 rounded-[40px] p-6">
              <div className="rounded-[40px] p-6 -m-1">
                {/* Header */}
                <div className="mb-8">
                  <p className="text-[12.8px] font-normal text-slate-500 mb-2">WELCOME BACK</p>
                  <h2 className="text-[25px] font-medium leading-[44px]">Log In to your Account</h2>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email */}
                  <div className="space-y-3">
                    <Label htmlFor="signin-email" className="text-base text-[#303030]">
                      Email Address<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="signin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="johndoe@gmail.com"
                      required
                      className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-3">
                    <Label htmlFor="signin-password" className="text-base text-[#303030]">
                      Password<span className="text-red-500"> *</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 pr-12 text-base"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked === true)}
                      />
                      <Label htmlFor="remember-me" className="text-[12.8px] font-normal cursor-pointer">
                        Remember me
                      </Label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className="text-[12.8px] font-medium text-[#424242] hover:underline"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isEmailLoading}
                    className="w-full h-[54px] rounded-[50px] bg-[#4669ed] hover:bg-[#2b42a3] text-white font-bold text-base mt-6"
                  >
                    {isEmailLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-[#F8FAFC] px-4 text-[12.8px] font-bold text-[#212121]">Or</span>
                  </div>
                </div>

                {/* Social Login Buttons */}
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOAuth('oauth_google')}
                    disabled={isGoogleLoading || !isLoaded}
                    className="w-full h-12 rounded-[27px] border border-[#eeeeee] bg-neutral-50 hover:bg-neutral-100 text-[#616161] text-[12.8px] font-normal justify-start gap-3"
                  >
                    {isGoogleLoading ? (
                      <>
                        <Loader2 className="w-[18px] h-[18px] animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Log In with Google
                      </>
                    )}
                  </Button>
                </div>

                {/* Sign Up Link */}
                <p className="text-center text-[12.8px] text-[#212121] mt-6">
                  <span className="font-normal">New User? </span>
                  <Link to="/sign-up" className="font-bold underline">
                    SIGN UP HERE
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

